import React, { useState } from 'react';
import axios from 'axios';
import './index.css';
import { GET_PRESIGNED_URL, SAVE_IMAGE_METADATA, DELETE_IMAGE } from '../../api/image.js';

const ImageUploader = () => {
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [originalFile, setOriginalFile] = useState(null);
  const [compressedFile, setCompressedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [uploadChoice, setUploadChoice] = useState('compressed');
  const [isUploading, setIsUploading] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleReset = () => {
    setFormData({ name: '', description: '' });
    setOriginalFile(null);
    setCompressedFile(null);
    setPreviewData(null);
    setUploadChoice('compressed');
    setIsUploading(false);
    setSuccessData(null);
    setErrorMessage(null);
  };

  const compressImage = (file, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => { img.src = e.target.result; };
      img.onerror = reject;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          if (!blob) return reject(new Error("Compression failed"));
          resolve(new File([blob], file.name, { type: blob.type, lastModified: Date.now() }));
        }, "image/jpeg", quality);
      };
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setOriginalFile(file);
    setErrorMessage(null);
    try {
      const compressed = await compressImage(file, 0.7);
      setCompressedFile(compressed);
      setPreviewData({
        preview: URL.createObjectURL(compressed),
        originalSize: (file.size / 1024).toFixed(2),
        compressedSize: (compressed.size / 1024).toFixed(2)
      });
    } catch (err) { console.error("Compression failed:", err); }
  };

  const handleUpload = async () => {
    if (!originalFile || !formData.name) return;
    setIsUploading(true);
    setErrorMessage(null);
    try {
      const fileToUpload = uploadChoice === 'compressed' ? compressedFile : originalFile;
      const path = `static/org/${fileToUpload.name}`;

      // 1. Get Pre-signed URL (Backend checks for existence)
      const { data: s3Data } = await axios.post(GET_PRESIGNED_URL, {
        path,
        contentType: fileToUpload.type
      });

      // 2. Direct S3 Upload
      await axios.put(s3Data.uploadUrl, fileToUpload, {
        headers: { 'Content-Type': fileToUpload.type }
      });

      // 3. Save Metadata
      await axios.post(SAVE_IMAGE_METADATA, { ...formData, url: s3Data.publicUrl });

      setSuccessData({ url: s3Data.publicUrl, name: formData.name });
    } catch (err) {
      if (err.response && err.response.status === 409) {
        setErrorMessage(err.response.data.message || "File already exists.");
      } else {
        setErrorMessage("Upload failed. Please try again.");
      }
    } finally { setIsUploading(false); }
  };

  const handleDelete = async (url) => {
    if (!window.confirm("Permanently delete this image?")) return;
    try {
      await axios.post(DELETE_IMAGE, { imageUrl: url });
      alert("Deleted successfully!");
      handleReset();
    } catch (err) { alert("Delete failed."); }
  };

  return (
    <div className="admin-upload-wrapper">
      <h2 className="admin-title">Frontend Image Portal</h2>

      {errorMessage && (
        <div className="error-overlay">
          <div className="error-popup">
            <button className="close-btn" onClick={() => setErrorMessage(null)}>×</button>
            <div className="error-icon">⚠️</div>
            <p className="error-text">{errorMessage}</p>
            <button className="btn-try-again" onClick={handleReset}>Try with another filename</button>
          </div>
        </div>
      )}

      {successData && (
        <div className="upload-success-box">
          <p className="success-msg">✅ Upload Successful!</p>
          <a href={successData.url} target="_blank" rel="noreferrer" className="url-link">{successData.url}</a>
          <div className="success-actions">
            <button className="btn-add-another" onClick={handleReset}>Upload Another</button>
            <button className="btn-delete-red" onClick={() => handleDelete(successData.url)}>Delete Image</button>
          </div>
        </div>
      )}

      {!successData && (
        <>
          <div className="admin-input-group">
            <input type="text" placeholder="Image Name" className="admin-field" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            <textarea placeholder="Description" className="admin-field admin-textarea" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            <input type="file" accept="image/*" className="admin-field admin-file-input" onChange={handleFileChange} />
          </div>

          {previewData && (
            <div className="preview-card">
              <img src={previewData.preview} alt="Preview" className="preview-image" />
              <div className="preview-controls-box">
                <div className="size-stats">
                  <p>Original: {previewData.originalSize} KB</p>
                  <p>Compressed: {previewData.compressedSize} KB</p>
                </div>
                <div className="selection-group">
                  <label className="radio-label">
                    <input type="radio" checked={uploadChoice === 'original'} onChange={() => setUploadChoice('original')} /> Original
                  </label>
                  <label className="radio-label">
                    <input type="radio" checked={uploadChoice === 'compressed'} onChange={() => setUploadChoice('compressed')} /> Compressed
                  </label>
                </div>
              </div>
            </div>
          )}

          <button className="btn-finalize" onClick={handleUpload} disabled={isUploading || !originalFile}>
            {isUploading ? "Uploading..." : "Save to S3 & DB"}
          </button>
        </>
      )}
    </div>
  );
};

export default ImageUploader;